import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons traced from `Email Configurations.dc.html`. The design draws each
 * one as an inline stroked SVG rather than a Material glyph, so the shapes live
 * here instead of pulling near-matches from `@mui/icons-material`.
 *
 * All of them inherit `currentColor`; size comes from the caller's `sx`.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Section-head glyphs are all drawn at stroke 1.9 in the design. */
const headStroke = 1.9;

/** "Email content" — open envelope. */
export const MailGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={headStroke}
      x="2"
      y="4"
      width="20"
      height="16"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={headStroke} d="M2 6l10 7 10-7" />
  </SvgIcon>
);

/** "Reminders & expiration" — clock. */
export const ClockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={headStroke} d="M12 8v4l3 3" />
    <circle {...strokeProps} strokeWidth={headStroke} cx="12" cy="12" r="9" />
  </SvgIcon>
);

/** "Signer permissions" — shield with a tick. */
export const ShieldCheckGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={headStroke}
      d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z"
    />
    <path {...strokeProps} strokeWidth={headStroke} d="M9 12l2 2 4-4" />
  </SvgIcon>
);

/** "Schedule sign request" — calendar. */
export const CalendarGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={headStroke}
      x="3"
      y="4"
      width="18"
      height="17"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={headStroke} d="M3 9h18M8 2v4M16 2v4" />
  </SvgIcon>
);

/** eSign-mode section — two people. */
export const PeopleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={headStroke}
      d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
    />
    <circle {...strokeProps} strokeWidth={headStroke} cx="9" cy="7" r="4" />
    <path
      {...strokeProps}
      strokeWidth={headStroke}
      d="M22 21v-2a4 4 0 0 0-3-3.87"
    />
  </SvgIcon>
);

/** Web-forms section — document with rules. */
export const DocumentGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={headStroke}
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
    />
    <path {...strokeProps} strokeWidth={headStroke} d="M14 3v5h5" />
    <path {...strokeProps} strokeWidth={headStroke} d="M9 13h6M9 17h4" />
  </SvgIcon>
);

/** Section-head expand chevron, also used by the schedule strip. */
export const ChevronDownGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.5} d="M6 9l6 6 6-6" />
  </SvgIcon>
);

/** Calendar beside the chosen schedule date — lighter stroke than the head. */
export const CalendarSmallGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="4"
      width="18"
      height="17"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M3 9h18" />
    <path {...strokeProps} strokeWidth={2} d="M8 2v4" />
    <path {...strokeProps} strokeWidth={2} d="M16 2v4" />
  </SvgIcon>
);

/** Closed envelope in the preview header. */
export const MailPreviewGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="2"
      y="4"
      width="20"
      height="16"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M2 6l10 7 10-7" />
  </SvgIcon>
);
